import React, { useMemo, useEffect, useRef } from 'react';
import useOnMobile from 'src/hooks/useOnMobile';
import { StyledComponent } from 'styled-components';

import {
  CloseButtonContainer,
  OverlayVideoBackground,
  OverlayVideoContent,
  OverlayVideoPlayer,
  WistiaPlayerContainer,
} from './overlay.video.styles';
import { VideoPlayerProps } from './VideoPlayer/VideoPlayer';
import { usePopupController } from 'src/hooks/usePopupController';
import WistiaPlayer from './WistiaPlayer';
interface DataLayerOptions {
  video_status: string;
  video_percent?: number;
}

export interface OverlayVideoProps {
  overlayVisible: boolean;
  setOverlayVisible: (value: boolean) => void;
  src?: string;
  srcMobile?: string;
  isVertical?: boolean;
  videoLabel: string;
  videoWrapper?: StyledComponent<({ ref, videoLabel, children, ...rest }: VideoPlayerProps) => JSX.Element, any>;
  onVideoOpen?: () => void;
  onVideoClose?: () => void;
  wistiaId?: string;
}

const OverlayVideo = ({
  overlayVisible,
  setOverlayVisible,
  src,
  srcMobile,
  videoLabel,
  isVertical,
  videoWrapper,
  onVideoOpen,
  onVideoClose,
  wistiaId,
}: OverlayVideoProps): JSX.Element => {
  const isMobile = useOnMobile();
  const VideoPlayer = videoWrapper ?? OverlayVideoPlayer;
  const videoRef = useRef<HTMLVideoElement>(null);
  const progressMarks = { '25': false, '50': false, '75': false, '100': false };

  const { pause, resume } = usePopupController();

  const addVerticalDesign = useMemo(() => {
    if (isVertical) return true;

    return isMobile && !!srcMobile;
  }, [isVertical, isMobile, srcMobile]);

  const videoSource = useMemo(() => {
    if (!srcMobile) return src;

    return !isMobile ? src : srcMobile;
  }, [isMobile, src, srcMobile]);

  useEffect(() => {
    if (overlayVisible) {
      // Pause the popup countdown when the video is open
      pause();
    } else {
      // Resume the popup countdown when the video is closed
      resume();
    }

    if (!videoRef.current) return; // video is not ready

    // adds event listeners for tracking
    videoRef.current.addEventListener('play', () => handleDataLayerTracking({ video_status: 'play' }));
    videoRef.current.addEventListener('pause', () => handleDataLayerTracking({ video_status: 'pause' }));
    videoRef.current.addEventListener('timeupdate', handleVideoProgress);
    return () => {
      if (!videoRef.current) return;

      videoRef.current.removeEventListener('play', null);
      videoRef.current.removeEventListener('pause', null);
      videoRef.current.removeEventListener('timeupdate', null);
    };
  }, [overlayVisible]);

  useEffect(() => {
    if (overlayVisible && onVideoOpen) onVideoOpen();
  }, [overlayVisible, onVideoOpen]);

  const handleOverlayClose = () => {
    if (onVideoClose) onVideoClose();

    setOverlayVisible(false);
  };

  const handleVideoProgress = () => {
    const video = videoRef?.current;

    if (!video) return; // video is not ready

    const percentageWatched = Math.floor((100 * video.currentTime) / video.duration);

    for (const [key, value] of Object.entries(progressMarks)) {
      const progressKey = key as keyof typeof progressMarks;
      if (percentageWatched >= parseInt(key) && !value) {
        progressMarks[progressKey] = true;
        if (parseInt(key) === 100) {
          handleDataLayerTracking({ video_status: 'complete', video_percent: parseInt(key) });
        } else {
          handleDataLayerTracking({ video_status: 'progress', video_percent: parseInt(key) });
        }
        break;
      }
    }
  };

  const handleDataLayerTracking = (videoEventData: DataLayerOptions) => {
    const videoLabel = videoSource.split('/').pop();

    if ((window as any).dataLayer) {
      (window as any).dataLayer?.push({
        event: 'video_interaction',
        video_provider: 'html5 video player', // hard coded value
        video_title: videoLabel, // Replace with the filename e.g. 'Forget_Everything_You_Know_.mp4
        ...videoEventData,
      });
    }
  };

  const renderVideoPlayer = () => (
    <VideoPlayer
      ref={videoRef}
      autoPlay
      controls
      videoLabel={videoLabel}
    >
      <source
        type='video/mp4'
        src={videoSource}
      />
    </VideoPlayer>
  );

  const renderWistiaPlayer = () => (
    <WistiaPlayerContainer isVertical={isVertical}>
      <WistiaPlayer
        videoId={wistiaId}
        autoPlay
        containerId={`overlay-video-wistia-id-${wistiaId}`}
      />
    </WistiaPlayerContainer>
  );

  return (
    overlayVisible && (
      <>
        <OverlayVideoBackground onClick={handleOverlayClose} />
        <OverlayVideoContent
          id='overlay-video-content'
          verticalDesign={addVerticalDesign}
        >
          <CloseButtonContainer onClick={handleOverlayClose}>×</CloseButtonContainer>
          {wistiaId ? renderWistiaPlayer() : renderVideoPlayer()}
        </OverlayVideoContent>
      </>
    )
  );
};

export default OverlayVideo;
